export type MarketingAttribution = {
  source?: string
  medium?: string
  campaign?: string
  campaignId?: string
  adSet?: string
  adSetId?: string
  ad?: string
  adId?: string
  content?: string
  term?: string
  gclid?: string
  fbclid?: string
  fbp?: string
  fbc?: string
  landingPage?: string
  referrer?: string
  platform?: string
}

const STORAGE_KEY = 'sa_marketing_attribution'
const LANDING_PAGE_MAX = 2048

function getCookie(name: string): string | undefined {
  try {
    if (typeof document === 'undefined') return undefined
    const parts = document.cookie.split(';')
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i].trim()
      if (!part) continue
      const eq = part.indexOf('=')
      if (eq === -1) continue
      const key = part.slice(0, eq)
      if (key !== name) continue
      const value = part.slice(eq + 1)
      try {
        return decodeURIComponent(value)
      } catch {
        return value
      }
    }
  } catch {
    // ignore
  }
  return undefined
}

function nonEmpty(value: string | null | undefined): string | undefined {
  if (value == null) return undefined
  const trimmed = String(value).trim()
  return trimmed ? trimmed : undefined
}

/** Meta recommended fbc format when cookie is missing: fb.1.{creationTimeMs}.{fbclid} */
export function buildFbcFromFbclid(fbclid: string): string {
  return `fb.1.${Date.now()}.${fbclid}`
}

function hasAttributionValues(attr: MarketingAttribution): boolean {
  return Object.values(attr).some(
    (v) => typeof v === 'string' && v.trim() !== ''
  )
}

export function buildMarketingAttributionFromPage():
  | MarketingAttribution
  | undefined {
  try {
    if (typeof window === 'undefined') return undefined

    const q = new URLSearchParams(window.location.search)
    const fbclid = nonEmpty(q.get('fbclid'))
    const fbp = nonEmpty(getCookie('_fbp'))
    let fbc = nonEmpty(getCookie('_fbc'))
    if (!fbc && fbclid) {
      fbc = buildFbcFromFbclid(fbclid)
    }

    let landingPage = window.location.href
    if (landingPage.length > LANDING_PAGE_MAX) {
      landingPage = landingPage.slice(0, LANDING_PAGE_MAX)
    }

    const attr: MarketingAttribution = {
      source: nonEmpty(q.get('utm_source')),
      medium: nonEmpty(q.get('utm_medium')),
      campaign: nonEmpty(q.get('utm_campaign')),
      campaignId:
        nonEmpty(q.get('utm_campaign_id')) ||
        nonEmpty(q.get('campaign_id')),
      adSet: nonEmpty(q.get('utm_adset')),
      adSetId: nonEmpty(q.get('adset_id')),
      ad: nonEmpty(q.get('utm_ad')),
      adId: nonEmpty(q.get('ad_id')),
      content: nonEmpty(q.get('utm_content')),
      term: nonEmpty(q.get('utm_term')),
      gclid: nonEmpty(q.get('gclid')),
      fbclid,
      fbp,
      fbc,
      landingPage: nonEmpty(landingPage),
      referrer: nonEmpty(document.referrer),
      platform: 'web',
    }

    // Drop undefined keys so we never send empty strings
    const cleaned: MarketingAttribution = {}
    ;(Object.keys(attr) as (keyof MarketingAttribution)[]).forEach((key) => {
      const value = attr[key]
      if (value != null && value !== '') {
        cleaned[key] = value
      }
    })

    return hasAttributionValues(cleaned) ? cleaned : undefined
  } catch {
    return undefined
  }
}

export function getStoredMarketingAttribution():
  | MarketingAttribution
  | undefined {
  try {
    if (typeof sessionStorage === 'undefined') return undefined
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return undefined
    const parsed = JSON.parse(raw) as MarketingAttribution
    if (!parsed || typeof parsed !== 'object') return undefined
    return hasAttributionValues(parsed) ? parsed : undefined
  } catch {
    return undefined
  }
}

/** First-touch: store once per browser session; never overwrite. */
export function captureMarketingAttributionOnce(): void {
  try {
    if (typeof sessionStorage === 'undefined') return
    if (getStoredMarketingAttribution()) return

    const built = buildMarketingAttributionFromPage()
    if (!built) return

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(built))
  } catch {
    // never break signup / navigation
  }
}
